import { assertNever } from '@eversports/utility-types'

import {
  CustomPaymentMethodType,
  PaymentMethodType,
  PaymentMethodVerificationMethod,
  PaymentResponse,
} from '../../typings/Payment'

import { ProcessPaymentFunction } from './../../data/context'
import confirmOrder from './confirm-order'
import handleSourcePayment from './handle-source-payment'
import handleSepaPayment from './handle-sepa-payment'
import preparePaymentAndPoll from './prepare-payment-and-poll'
import setupRecurringPaymentMethodAndPoll from './setup-recurring-payment-method-and-poll'
import confirmPaymentAndPoll from './confirm-payment-and-poll'

const mapVerifyWithToPaymentMethod = (verifyWith: PaymentMethodVerificationMethod) => {
  switch (verifyWith) {
    case PaymentMethodVerificationMethod.PAYMENT_METHOD_IDEAL:
      return { type: PaymentMethodType.PAYMENT_METHOD_IDEAL, saveCard: [] }
    case PaymentMethodVerificationMethod.PAYMENT_METHOD_SOFORT:
      return { type: PaymentMethodType.PAYMENT_METHOD_SOFORT, saveCard: [] }
    default:
      return assertNever(verifyWith)
  }
}

const processPayment: ProcessPaymentFunction = async (
  paymentProvider,
  paymentAPI,
  options,
): Promise<PaymentResponse> => {
  if (!paymentProvider.stripe) {
    throw new Error('Stripe was not correctly loaded')
  }

  // add the timestamp of the last payment attempt to be able to
  // - detect payment state transitions to the same state as before (f.e.: FAILED => FAILED)
  const lastPaymentAttempt = new Date()

  const { orderId, selectedPaymentMethod } = options

  if (!selectedPaymentMethod.type) {
    throw new Error('Payment function was called without a selected payment method type')
  }

  switch (selectedPaymentMethod.type) {
    case CustomPaymentMethodType.PAYMENT_METHOD_OFFLINE:
      return confirmOrder(paymentAPI, orderId)
    case PaymentMethodType.PAYMENT_METHOD_SEPA_ONLINE:
      if (options.payment.canSetupRecurringPaymentMethod && options.isSetupRecurringPaymentMethodApiEnabled) {
        if (selectedPaymentMethod.pspPaymentMethodId) {
          return confirmPaymentAndPoll(paymentAPI, { orderId, options, lastPaymentAttempt })
        } else {
          return setupRecurringPaymentMethodAndPoll(paymentProvider, paymentAPI, {
            orderId,
            options,
            lastPaymentAttempt,
          })
        }
        // TODO: remove the functions bellow once we rollout the new setup recurring payment method to everyone
      } else {
        if (selectedPaymentMethod.bankAccountHolder || selectedPaymentMethod.pspPaymentMethodId) {
          return handleSepaPayment(paymentProvider, paymentAPI, options, lastPaymentAttempt)
        }
        if (options.verifyWith) {
          return handleSourcePayment(
            paymentProvider,
            paymentAPI,
            { ...options, selectedPaymentMethod: mapVerifyWithToPaymentMethod(options.verifyWith) },
            lastPaymentAttempt,
          )
        }
      }
      throw new Error('Unexpected SEPA payment. Use one of (existing payment method/new bank account/verifyWith) ')
    case PaymentMethodType.PAYMENT_METHOD_PAYPAL:
    case PaymentMethodType.PAYMENT_METHOD_BANCONTACT:
    case PaymentMethodType.PAYMENT_METHOD_IDEAL:
    case PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD:
    case PaymentMethodType.PAYMENT_METHOD_GIROPAY:
    case PaymentMethodType.PAYMENT_METHOD_EPS:
    case PaymentMethodType.PAYMENT_METHOD_TWINT:
    case PaymentMethodType.PAYMENT_METHOD_SOFORT: {
      return preparePaymentAndPoll(paymentProvider, paymentAPI, {
        orderId,
        selectedPaymentMethod,
        lastPaymentAttempt,
        options,
      })
    }
    case PaymentMethodType.PAYMENT_METHOD_MULTIBANCO:
      throw new Error('Multibanco payment method is not supported')
    default:
      return assertNever(selectedPaymentMethod.type)
  }
}

export default processPayment
